/**
 * Mulberry Shop — Auth store
 *
 * Власник СЕСІЇ: хто увійшов, з якими claims, і як увійти/вийти
 * (email/пароль, Google, телефон).
 *
 * Механіки телефонного OTP тут НЕМАЄ — верифаєр, гальмо, мапінг помилок і
 * телеметрія живуть у `composables/auth/usePhoneOtp.ts`, спільному з модалкою
 * зміни номера в профілі. Телефонні методи нижче — тонка обгортка: вони
 * додають рівно те, чого композабл не знає, — оновлення сесії стора.
 */

// stores/auth.js
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useNuxtApp } from "#app";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
  EmailAuthProvider,
  linkWithCredential,
  browserPopupRedirectResolver,
  GoogleAuthProvider,
  signInWithPopup,
  getIdTokenResult,
  signInWithPhoneNumber,
} from "firebase/auth";
import type { ConfirmationResult, ParsedToken, User } from "firebase/auth";
import * as Sentry from "@sentry/nuxt";
import { toE164, PHONE_FORMAT_HINT } from "~/utils/phone";
import { usePhoneOtp } from "~/composables/auth/usePhoneOtp";
// Ре-експорту тут НЕМАЄ навмисно: `utils/` у Nuxt авто-імпортується, тож
// `PhoneAuthError` і так доступний глобально, а друге джерело того самого
// імені дає «Duplicated imports» на білді.
import { PhoneAuthError } from "~/utils/phoneAuthErrors";

/**
 * Уся механіка телефонного OTP — верифаєр, мапінг помилок, гальмо і
 * телеметрія — живе в `composables/auth/usePhoneOtp.ts`. Тут лишилась
 * тільки прив'язка до сесії стора: хто увійшов і з якими claims.
 */

// ============================================================
// Pinia store
// ============================================================
export const useAuthStore = defineStore("auth", () => {
  const { $auth } = useNuxtApp();

  // Reactive auth state
  // Параметри типів обов'язкові: ref(null) вивелося б як Ref<null>, і будь-яке
  // присвоєння User не компілювалось би.
  const user = ref<User | null>(null);
  const claims = ref<ParsedToken>({});
  const loading = ref(true);

  // Claims/roles (e.g., admin flag)
  let _resolveReady: (() => void) | undefined;
  const _ready = new Promise<void>((r) => (_resolveReady = r));
  const ready = computed(() => loading.value === false);
  const isAuthed = computed(() => !!user.value);
  const isAdmin = computed(() => !!claims.value?.admin);

  // Keep store state in sync with Firebase Auth session
  async function refreshClaims(): Promise<void> {
    if (!user.value) {
      claims.value = {};
      return;
    }
    const res = await getIdTokenResult(user.value, true);
    claims.value = res?.claims || {};
  }

  if (import.meta.client) {
    onAuthStateChanged($auth, async (u: User | null) => {
      user.value = u;
      try {
        // Тільки uid: sendDefaultPii вимкнено, телефон/пошта сюди не йдуть.
        Sentry.setUser(u ? { id: u.uid } : null);
      } catch {}
      await refreshClaims();
      loading.value = false;
      try {
        // Було виразом-побічним ефектом; у .ts на це діє no-unused-expressions.
        if (_resolveReady) _resolveReady();
      } catch {}
    });
  }

  // Await initial auth resolution (useful for middleware/guards)
  function whenReady(): Promise<void> {
    return ready.value ? Promise.resolve() : _ready;
  }

  async function forceRefreshClaims(): Promise<void> {
    if (!user.value) return;
    await user.value.getIdToken(true);
    await refreshClaims();
  }

  // Email/password flows
  async function register({
    email,
    password,
    displayName,
  }: {
    email: string;
    password: string;
    displayName?: string;
  }): Promise<void> {
    const cred = await createUserWithEmailAndPassword($auth, email, password);
    if (displayName) await updateProfile(cred.user, { displayName });
    await sendEmailVerification(cred.user);
    user.value = cred.user;
    await refreshClaims();
  }

  /**
   * Додати email+пароль до акаунта, який досі мав лише телефон.
   *
   * Саме link, а не створення нового користувача: інакше людина отримала б
   * ДРУГИЙ акаунт і втратила доступ до власних замовлень. Після прив'язки в
   * той самий профіль можна зайти обома способами.
   */
  async function linkEmailPassword({
    email,
    password,
  }: {
    email: string;
    password: string;
  }): Promise<void> {
    const current = $auth.currentUser;
    if (!current) throw new Error("auth/no-current-user");

    const credential = EmailAuthProvider.credential(
      String(email || "").trim(),
      String(password || ""),
    );
    const cred = await linkWithCredential(current, credential);
    user.value = cred.user;
    // Непідтверджений email усе одно дозволяє вхід, але лист — гігієна.
    try {
      await sendEmailVerification(cred.user);
    } catch {
      /* лист не критичний: пара email+пароль уже працює */
    }
    await refreshClaims();
  }

  /**
   * Лист для скидання пароля.
   *
   * Навмисно НЕ розрізняємо «такого email немає» і «лист надіслано»: інакше
   * форма стає оракулом, який підтверджує, чи зареєстрований конкретний email.
   * Сторінка показує однакове повідомлення в обох випадках.
   */
  async function sendPasswordReset(email: string): Promise<void> {
    const addr = String(email || "").trim();
    if (!addr) throw new Error("auth/missing-email");
    try {
      await sendPasswordResetEmail($auth, addr);
    } catch (e) {
      const code = String((e as { code?: unknown })?.code || "");
      // Мовчимо ТІЛЬКИ про «такого користувача немає» — це і є захист від
      // перебору. `invalid-email` ковтати не можна: там людина зробила
      // одруківку, і «лист надіслано» відправить її чекати назавжди.
      if (code.includes("auth/user-not-found")) return;
      throw e;
    }
  }

  async function loginEmail({
    email,
    password,
  }: {
    email: string;
    password: string;
  }): Promise<void> {
    const cred = await signInWithEmailAndPassword($auth, email, password);
    user.value = cred.user;
    await refreshClaims();
  }

  // OAuth flows
  async function loginGoogle(): Promise<void> {
    const provider = new GoogleAuthProvider();
    // Резолвер передаємо ЯВНО: у плагіні auth ініціалізується без нього, щоб
    // не тягнути gapi-iframe на кожній мобільній сторінці (див.
    // plugins/firebase.client.ts). Без цього аргументу тут буде
    // auth/argument-error.
    const cred = await signInWithPopup(
      $auth,
      provider,
      browserPopupRedirectResolver,
    );
    user.value = cred.user;
    await refreshClaims();
  }

  // Sign out + cleanup transient phone auth state
  async function logout(): Promise<void> {
    await signOut($auth);
    user.value = null;
    claims.value = {};
    otp.reset();
  }

  // ── Телефонний OTP ────────────────────────────────────────────────────
  // Механіка — в канонічному композаблі; тут лишається рівно одна річ,
  // якої він не знає: вхід оновлює сесію стора.
  const phoneSession = ref({ phoneE164: "" });

  const otp = usePhoneOtp<ConfirmationResult, User>({
    flow: "signin",
    send: (phoneE164, verifier) =>
      signInWithPhoneNumber($auth, phoneE164, verifier),
    confirm: async (confirmation, code) => {
      const { user: u } = await confirmation.confirm(code);
      user.value = u;
      await refreshClaims();
      return u;
    },
  });

  async function startPhoneSignIn(
    rawPhone: unknown,
  ): Promise<boolean | undefined> {
    if (import.meta.server) return;
    // UA-only: toE164 повертає "+380XXXXXXXXX" лише для повного номера.
    // Фінальну валідацію все одно робить Firebase.
    const phone = toE164(rawPhone);
    if (!phone) {
      throw new PhoneAuthError("phone/invalid-format", PHONE_FORMAT_HINT);
    }

    await otp.sendCode(phone);
    phoneSession.value.phoneE164 = phone;
    return true;
  }

  function confirmPhoneCode(code: unknown): Promise<User> {
    return otp.confirmCode(code);
  }

  // Allow UI to cancel OTP flow and reset transient state.
  function cancelPhoneSignIn(): void {
    otp.reset();
    phoneSession.value.phoneE164 = "";
  }

  return {
    user,
    claims,
    loading,
    ready,
    isAuthed,
    isAdmin,
    whenReady,
    refreshClaims,
    forceRefreshClaims,
    register,
    loginEmail,
    sendPasswordReset,
    linkEmailPassword,
    loginGoogle,
    logout,
    phoneSession,
    startPhoneSignIn,
    confirmPhoneCode,
    cancelPhoneSignIn,
  };
});
